import styled from "styled-components"

export const OfferingStyles = styled.main`
  display: block;
  overflow: hidden;

  /* ── full-bleed bands; alternate ink / coal ───────────────── */
  .band {
    padding-block: clamp(4.5rem, 12vh, 11rem);
    position: relative;
  }
  .band--coal {
    background-color: var(--color-coal);
  }
  .band__inner {
    width: 100%;
    max-width: 1320px;
    margin-inline: auto;
    padding-inline: var(--borderSpacing);
    position: relative;
    z-index: 1;
  }
  .band__head {
    max-width: 820px;
    margin-bottom: clamp(2.5rem, 6vh, 5rem);

    .eyebrow {
      margin-bottom: 1.5rem;
    }
    h2 {
      font-size: clamp(2rem, 4.6vw, 3.7rem);
      line-height: 1.02;
      margin: 0 0 1.4rem;
    }
    .band__lead {
      font-size: var(--text-lead);
      line-height: 1.5;
      color: var(--color-ash);
      margin: 0;
    }
  }

  /* ── hero ─────────────────────────────────────────────────── */
  .o-hero {
    padding-top: clamp(150px, 22vh, 290px);
    padding-bottom: clamp(3.5rem, 10vh, 8rem);
    position: relative;

    /* amber stage-glow, upper-right */
    &::before {
      content: "";
      position: absolute;
      top: -10%;
      right: -5%;
      width: 50vw;
      height: 50vw;
      max-width: 760px;
      max-height: 760px;
      background: radial-gradient(
        circle,
        var(--color-glow),
        rgba(11, 10, 8, 0) 62%
      );
      pointer-events: none;
    }
  }
  .o-hero__inner {
    width: 100%;
    max-width: 1320px;
    margin-inline: auto;
    padding-inline: var(--borderSpacing);
    position: relative;
    z-index: 1;
  }
  .o-hero .eyebrow {
    margin-bottom: 2rem;
  }
  .o-hero h1 {
    font-size: clamp(2.75rem, 8.5vw, 6.75rem);
    line-height: 0.95;
    margin: 0;
    max-width: 16ch;
  }
  .o-hero h1 .accent {
    color: var(--color-gold-500);
  }
  .o-hero__lead {
    margin: clamp(1.75rem, 4vh, 2.75rem) 0 0;
    max-width: 640px;
    font-size: var(--text-lead);
    line-height: 1.5;
    color: var(--color-ash);
  }
  .o-hero__who {
    margin-top: 2.25rem;
    font-family: var(--font-sans);
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--color-faint);
  }
  .o-hero__who b {
    color: var(--color-gold-500);
    font-weight: 700;
  }
  .o-hero__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: clamp(2.25rem, 5vh, 3.25rem);
  }

  /* ── "what's included": rounded cards w/ gold numerals ────── */
  .included {
    display: grid;
    grid-template-columns: 1fr;
    gap: clamp(0.9rem, 1.5vw, 1.25rem);

    @media (min-width: 600px) {
      grid-template-columns: 1fr 1fr;
    }
    @media (min-width: 1000px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  .included__item {
    border: 1px solid var(--color-line);
    border-radius: 16px;
    background: var(--color-panel);
    padding: clamp(1.75rem, 3vw, 2.5rem);
    transition: border-color 0.4s var(--ease-power),
      transform 0.4s var(--ease-power), box-shadow 0.4s var(--ease-power);

    .included__num {
      font-family: var(--font-display);
      font-weight: 600;
      font-size: 1.9rem;
      line-height: 1;
      color: var(--color-gold-500);
    }
    h3 {
      font-size: var(--h4);
      margin: 1.25rem 0 0.7rem;
    }
    p {
      margin: 0;
      color: var(--color-ash);
      font-size: var(--p);
    }

    &:hover {
      border-color: var(--color-gold-700);
      transform: translateY(-4px);
      box-shadow: 0 22px 50px -26px rgba(0, 0, 0, 0.85),
        0 0 0 1px rgba(231, 163, 56, 0.1);
    }
  }

  /* ── process: a gold "progress" timeline ──────────────────── */
  .process {
    display: grid;
    grid-template-columns: 1fr;
    gap: clamp(1.75rem, 3vw, 2.75rem);

    @media (min-width: 600px) {
      grid-template-columns: 1fr 1fr;
    }
    @media (min-width: 1000px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  .process__step {
    position: relative;
    padding-top: 1.6rem;
    border-top: 2px solid var(--color-line);

    &::before {
      content: "";
      position: absolute;
      top: -2px;
      left: 0;
      width: 44px;
      height: 2px;
      background: var(--color-gold-500);
    }

    .process__num {
      font-family: var(--font-display);
      font-weight: 600;
      font-size: clamp(2.25rem, 4.5vw, 3.25rem);
      line-height: 1;
      color: var(--color-gold-500);
    }
    h3 {
      font-size: var(--h5);
      margin: 1rem 0 0.6rem;
    }
    p {
      margin: 0;
      color: var(--color-ash);
      font-size: var(--p);
    }
  }

  /* ── what we need from you ────────────────────────────────── */
  .needs {
    display: grid;
    grid-template-columns: 1fr;
    gap: clamp(2rem, 5vw, 4rem);

    @media (min-width: 900px) {
      grid-template-columns: 1.05fr 1fr;
      align-items: start;
    }
  }
  .needs__list {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: flex;
      align-items: baseline;
      gap: 1rem;
      padding: 1.15rem 0;
      border-bottom: 1px solid var(--color-line);
      color: var(--color-paper);

      &:first-child {
        border-top: 1px solid var(--color-line);
      }
      &::before {
        content: "—";
        color: var(--color-gold-500);
        font-weight: 700;
      }
    }
  }
  .reassure {
    background: var(--color-panel);
    border: 1px solid var(--color-line);
    border-radius: 16px;
    padding: clamp(1.75rem, 3vw, 2.5rem);
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 3px;
      height: 100%;
      background: var(--color-gold-500);
    }

    p {
      margin: 0;
      font-family: var(--font-display);
      font-size: var(--text-title);
      line-height: 1.12;
      color: var(--color-paper);
    }
    .reassure__sub {
      font-family: var(--font-sans);
      font-size: var(--p);
      color: var(--color-ash);
      margin-top: 1.1rem;
    }
  }

  /* ── pricing ──────────────────────────────────────────────── */
  .pricing__feature {
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
    background: var(--color-panel);
    border: 1px solid var(--color-gold-700);
    border-radius: 20px;
    padding: clamp(1.9rem, 4vw, 3rem);
    margin-bottom: calc(var(--gap) * 1.1);
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -40%;
      right: -10%;
      width: 380px;
      height: 380px;
      background: radial-gradient(
        circle,
        var(--color-glow),
        rgba(0, 0, 0, 0) 65%
      );
      pointer-events: none;
    }

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }

    .pricing__feature-main {
      position: relative;
      z-index: 1;
      h3 {
        font-size: var(--h3);
        margin: 0 0 0.6rem;
      }
      p {
        margin: 0;
        color: var(--color-ash);
        max-width: 46ch;
      }
    }
    .pricing__feature-side {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      align-items: flex-start;

      @media (min-width: 768px) {
        align-items: flex-end;
        text-align: right;
      }
    }
    .pricing__amount {
      font-family: var(--font-display);
      font-weight: 600;
      font-size: clamp(2.75rem, 5.5vw, 3.75rem);
      line-height: 1;
      color: var(--color-gold-500);
    }
  }

  .pricing__rows {
    border-top: 1px solid var(--color-line);
  }
  .pricing__row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: clamp(1.3rem, 2.5vw, 1.9rem) 0;
    border-bottom: 1px solid var(--color-line);

    @media (min-width: 640px) {
      flex-direction: row;
      align-items: baseline;
      justify-content: space-between;
      gap: 2rem;
    }

    .pricing__row-main {
      h4 {
        margin: 0 0 0.4rem;
        font-size: var(--h5);
      }
      p {
        margin: 0;
        color: var(--color-ash);
        font-size: var(--p);
        max-width: 60ch;
      }
    }
    .pricing__row-price {
      font-family: var(--font-display);
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--color-gold-500);
      white-space: nowrap;

      &[data-muted="true"] {
        font-family: var(--font-sans);
        color: var(--color-faint);
        text-transform: uppercase;
        font-size: var(--text-kicker);
        font-weight: 600;
        letter-spacing: 0.16em;
      }
    }
  }

  /* ── faqs ─────────────────────────────────────────────────── */
  .o-faqs {
    max-width: 880px;
  }

  /* ── closing cta ──────────────────────────────────────────── */
  .o-cta {
    text-align: center;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 70vw;
      height: 70vw;
      max-width: 820px;
      max-height: 820px;
      background: radial-gradient(
        circle,
        var(--color-glow),
        rgba(0, 0, 0, 0) 60%
      );
      pointer-events: none;
    }
  }
  .o-cta__inner {
    width: 100%;
    max-width: 940px;
    margin-inline: auto;
    padding-inline: var(--borderSpacing);
    position: relative;
    z-index: 1;
  }
  .o-cta h2 {
    font-size: clamp(2.25rem, 6vw, 4.75rem);
    line-height: 1;
    margin: 1.5rem 0 0;
  }
  .o-cta .accent {
    color: var(--color-gold-500);
  }
  .o-cta__lead {
    margin: 1.75rem auto 0;
    max-width: 560px;
    color: var(--color-ash);
    font-size: var(--text-lead);
  }
  .o-cta__btns {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    margin-top: clamp(2.25rem, 5vh, 3.25rem);
  }
`
